/**
 * Shared model definitions for both frontend and backend
 * This file is imported by both the React frontend and Firebase Functions backend
 */

// LLM families
export const LLM_FAMILIES = {
  GPT: 'gpt',
  CLAUDE: 'claude',
  GEMINI: 'gemini',
};

// LLM specific models
export const LLM_MODELS = {
  // OpenAI models
  GPT_45_PREVIEW: 'gpt-4.5-preview-2025-02-27',
  GPT_4O: 'gpt-4o-2024-11-20',
  GPT_4O_MINI: 'gpt-4o-mini-2024-07-18',
  
  // Claude models
  CLAUDE_3_7_SONNET: 'claude-3-7-sonnet-20250219',
  CLAUDE_3_5_SONNET: 'claude-3-5-sonnet-20241022',

  // Gemini models
  GEMINI_FLASH_LITE: 'gemini-2.0-flash-lite',
  GEMINI_FLASH: 'gemini-2.0-flash',
  GEMINI_PRO_EXP: 'gemini-2.0-pro-exp-02-05',
}

// Map models to their families
export const MODEL_TO_FAMILY_MAP = {
  [LLM_MODELS.GPT_45_PREVIEW]: LLM_FAMILIES.GPT,  
  [LLM_MODELS.GPT_4O]: LLM_FAMILIES.GPT,
  [LLM_MODELS.GPT_4O_MINI]: LLM_FAMILIES.GPT,
  
  [LLM_MODELS.CLAUDE_3_7_SONNET]: LLM_FAMILIES.CLAUDE,
  [LLM_MODELS.CLAUDE_3_5_SONNET]: LLM_FAMILIES.CLAUDE,

  [LLM_MODELS.GEMINI_FLASH_LITE]: LLM_FAMILIES.GEMINI,
  [LLM_MODELS.GEMINI_FLASH]: LLM_FAMILIES.GEMINI,
  [LLM_MODELS.GEMINI_PRO_EXP]: LLM_FAMILIES.GEMINI,
}

// Default models if not specified
export const DEFAULT_MODELS = {
  [LLM_FAMILIES.GPT]: LLM_MODELS.GPT_4O_MINI,
  [LLM_FAMILIES.CLAUDE]: LLM_MODELS.CLAUDE_3_5_SONNET,
  [LLM_FAMILIES.GEMINI]: LLM_MODELS.GEMINI_FLASH_LITE,
};

// Frontend-specific model configs (to be imported only in frontend)
export const getLLMConfig = () => ({
  // OpenAI models
  [LLM_MODELS.GPT_45_PREVIEW]: {
      name: 'GPT 4.5 Preview',
      family: LLM_FAMILIES.GPT,
      requiresPro: false,
      requiresUltimate: true, // Only Ultimate users can access this model
      available: true,
  },
  [LLM_MODELS.GPT_4O]: {
      name: 'GPT-4o',
      family: LLM_FAMILIES.GPT,
      requiresPro: true,
      requiresUltimate: false,
      available: true,
      limitedUsage: true, // Indicates this model has usage limits for Pro tier
      monthlyLimit: 20, // 20 chats per month for Pro tier
  },
  [LLM_MODELS.GPT_4O_MINI]: {
      name: 'GPT-4o mini',
      family: LLM_FAMILIES.GPT,
      requiresPro: false,
      requiresUltimate: false,
      default: true,
      available: true,
  },

  // Claude models
  [LLM_MODELS.CLAUDE_3_7_SONNET]: {
      name: 'Claude 3.7 Sonnet',
      family: LLM_FAMILIES.CLAUDE,
      requiresPro: false,
      requiresUltimate: true, // Only Ultimate users can access this model
      default: false,
      available: true,
  },
  [LLM_MODELS.CLAUDE_3_5_SONNET]: {
      name: 'Claude 3.5 Sonnet',
      family: LLM_FAMILIES.CLAUDE,
      requiresPro: true,
      requiresUltimate: false,
      default: true,
      available: true,
      limitedUsage: true, // Indicates this model has usage limits for Pro tier
      monthlyLimit: 20, // 20 chats per month for Pro tier
  },
  // Gemini models
  [LLM_MODELS.GEMINI_FLASH_LITE]: {
      name: 'Gemini Flash Lite',
      family: LLM_FAMILIES.GEMINI,
      requiresPro: true,
      requiresUltimate: false,
      default: false,
      available: true,
  },
  [LLM_MODELS.GEMINI_FLASH]: {
      name: 'Gemini Flash',
      family: LLM_FAMILIES.GEMINI,
      requiresPro: false,
      requiresUltimate: true,
      default: false,
      available: true,
  },
  [LLM_MODELS.GEMINI_PRO_EXP]: {
      name: 'Gemini Pro Experimental',
      family: LLM_FAMILIES.GEMINI,
      requiresPro: false,
      requiresUltimate: true,
      default: false,
      available: true,
  },
});

// Get available models
export const getAvailableModels = () => {
  return Object.entries(getLLMConfig())
    .filter(([_, config]) => config.available !== false)
    .reduce((acc, [model, config]) => {
      acc[model] = config;
      return acc;
    }, {});
};

// Get default model
export const getDefaultModel = () => {
  const model = Object.entries(getLLMConfig()).find(([_, config]) => config.default);
  return model ? model[0] : LLM_MODELS.GPT_4O_MINI;
};